import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileSpreadsheet, Loader2, Mail } from "lucide-react";
import { api, errMsg, fmtDateTime, openBlob } from "../../lib/api";
import { Field, Chip, focusRing } from "../common";
import { useScrollTo } from "../../hooks/useFocusParam";
import { MonthField } from "../DateFields";
import { currentPeriod } from "../../lib/labels";
import { useAuth } from "../../context/AuthContext";

export function ExcelPanel({ focusId = "" }) {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const [period, setPeriod] = useState(currentPeriod());
  const [dayType, setDayType] = useState("semaine");
  const [busy, setBusy] = useState(false);
  const [list, setList] = useState([]);
  const [cfg, setCfg] = useState(null);
  useScrollTo(focusId, list.length);
  const load = () => api.get("/excel/exports").then((r) => setList(r.data));
  useEffect(() => {
    load();
    api.get("/excel/settings").then((r) => setCfg(r.data));
  }, []);
  const gen = async () => {
    setBusy(true);
    try { await api.post("/excel/generate", { period, day_type: dayType }); toast.success("Classeurs générés"); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
    load();
  };
  const regen = async (x) => {
    setBusy(true);
    try {
      const { data } = await api.post("/excel/generate", { period: x.period, day_type: x.day_type });
      if (data.recalculated === false) toast.error("Régénéré, mais les prix ne sont toujours pas calculés");
      else toast.success(`Export régénéré (v${data.version}) avec prix calculés`);
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
    load();
  };
  const mail = async (id) => {
    try { await api.post(`/excel/exports/${id}/email`); toast.success("Classeurs envoyés par e-mail"); } catch (e) { toast.error(errMsg(e)); }
    load();
  };
  const rename = async (x, variant) => {
    const n = window.prompt("Nouveau nom du fichier", x.names[variant]);
    if (!n) return;
    try { await api.patch(`/excel/exports/${x.id}`, { [variant]: n }); toast.success("Fichier renommé"); } catch (e) { toast.error(errMsg(e)); }
    load();
  };
  const replace = async (x, variant, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("variant", variant);
    fd.append("file", file);
    try { await api.post(`/excel/exports/${x.id}/replace`, fd); toast.success("Version modifiée enregistrée"); } catch (e) { toast.error(errMsg(e)); }
    load();
  };
  return (
    <div className="space-y-4">
      <div className="tl-card flex flex-wrap gap-3 items-end">
        <Field label="Mois travaillé" className="w-44"><MonthField value={period} onChange={setPeriod} testid="excel-period" /></Field>
        <Field label="Classeur" className="w-44"><select className="tl-select" value={dayType} onChange={(e) => setDayType(e.target.value)} data-testid="excel-daytype"><option value="semaine">Semaine</option><option value="samedi">Samedi</option></select></Field>
        <button className="tl-btn tl-btn-primary" onClick={gen} disabled={busy} data-testid="excel-generate-button">{busy ? <Loader2 className="animate-spin" /> : <FileSpreadsheet size={18} />} Générer les classeurs</button>
      </div>
      {cfg && (
        <div className="tl-card text-sm space-y-1" data-testid="excel-settings">
          <div><b>Modèles :</b> semaine = {cfg.templates.semaine} · samedi = {cfg.templates.samedi}</div>
          <ul className="list-disc ml-5">{cfg.rules.map((r) => <li key={r}>{r}</li>)}</ul>
          <div><b>Feuilles sous-traitants (société validée → feuille) :</b> {cfg.companies.map((c) => `${c.name} → ${c.validated ? c.sheet_name || "aucune" : "non validée"}`).join(" · ")}</div>
        </div>
      )}
      <div className="space-y-2">
        {list.some((x) => x.recalculated === false) && (
          <div className="rounded-lg border-2 border-red-600 bg-red-50 p-3 text-sm font-semibold text-red-800" data-testid="excel-uncomputed-banner">
            ALERTE : {list.filter((x) => x.recalculated === false).length} export(s) sans prix calculés (outil de calcul du serveur indisponible). Ouvrez le fichier dans Excel et recalculez, ou régénérez l'export.
          </div>
        )}
        {list.map((x) => (
          <div key={x.id} data-focus-id={x.id} className={`tl-card flex flex-wrap gap-2 items-center ${x.recalculated === false ? "!border-2 !border-red-600 bg-red-50" : ""} ${focusRing(focusId === x.id)}`} data-testid={`excel-export-${x.id}`}>
            {x.recalculated === false && <Chip tone="red" testid={`uncomputed-${x.id}`}>Prix non calculés</Chip>}
            {x.recalculated === false && <button className="tl-btn tl-btn-danger tl-btn-sm" disabled={busy} onClick={() => regen(x)} data-testid={`regenerate-${x.id}`}>{busy ? <Loader2 className="animate-spin" size={14} /> : null} Régénérer</button>}
            <span className="font-semibold">{x.period} · {x.day_type}</span>
            {x.version && <span className="tl-mono">v{x.version}</span>}
            <Chip tone={x.status === "termine" ? "green" : x.status === "echec" ? "red" : "amber"}>{x.status}</Chip>
            <span className="text-xs text-slate-500">{fmtDateTime(x.created_at)} · {x.lines ?? 0} ligne(s)</span>
            {x.error && <span className="text-xs text-red-700">{x.error}</span>}
            {x.email_status && (
              <span className={`text-xs ${x.email_status === "envoye" ? "text-emerald-700" : "text-red-700"}`} data-testid={`email-status-${x.id}`}>
                {x.email_status === "envoye" ? `E-mail envoyé ${fmtDateTime(x.email_at)}` : `E-mail non envoyé : ${x.email_error}`}
              </span>
            )}
            {x.status === "termine" && (
              <div className="ml-auto flex gap-2">
                {isAdmin && <button className="tl-btn tl-btn-dark tl-btn-sm" onClick={() => openBlob(`/excel/exports/${x.id}/download?variant=internal`, x.names.internal, true)} data-testid={`download-internal-${x.id}`}>Classeur maître</button>}
                <button className="tl-btn tl-btn-ghost tl-btn-sm" onClick={() => openBlob(`/excel/exports/${x.id}/download?variant=ups`, x.names.ups, true)} data-testid={`download-ups-${x.id}`}>Export UPS</button>
                <button className="tl-btn tl-btn-ghost tl-btn-sm" onClick={() => mail(x.id)} data-testid={`email-export-${x.id}`}><Mail size={14} /> Envoyer par e-mail</button>
              </div>
            )}
            {x.status === "termine" && (
              <div className="w-full flex flex-wrap gap-2 text-xs items-center border-t pt-2">
                {[["internal", "Classeur maître"], ["ups", "Export UPS"]].filter(([v]) => isAdmin || v === "ups").map(([v, l]) => (
                  <span key={v} className="flex gap-1 items-center">
                    <span className="tl-mono">{x.names[v]}</span>{x.modified?.[v] && <Chip tone="amber">modifié par {x.modified[v].by}</Chip>}
                    <button className="underline" onClick={() => rename(x, v)} data-testid={`rename-${v}-${x.id}`}>Renommer</button>
                    <label className="underline cursor-pointer">Remplacer<input type="file" accept=".xlsx" className="hidden" onChange={(e) => replace(x, v, e.target.files[0])} data-testid={`replace-${v}-${x.id}`} /></label>
                  </span>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export function PeriodsPanel({ isAdmin }) {
  const [rows, setRows] = useState([]);
  const [p, setP] = useState(currentPeriod());
  const load = () => api.get("/periods").then((r) => setRows(r.data));
  useEffect(() => {
    load();
  }, []);
  const act = async (period, action) => {
    try { await api.post(`/periods/${period}/${action}`); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-3">
      {isAdmin && (
        <div className="tl-card flex gap-2 items-end">
          <Field label="Période" className="w-44"><MonthField value={p} onChange={setP} testid="period-input" /></Field>
          <button className="tl-btn tl-btn-dark" onClick={() => act(p, "close")} data-testid="close-period-button">Clôturer</button>
        </div>
      )}
      {rows.map((r) => (
        <div key={r.period} className="tl-card flex items-center gap-2" data-testid={`period-${r.period}`}>
          <b>{r.period}</b><Chip tone={r.closed ? "red" : "green"}>{r.closed ? "Clôturée" : "Ouverte"}</Chip>
          <span className="text-xs text-slate-500">{r.updated_by} · {fmtDateTime(r.updated_at)}</span>
          {isAdmin && <button className="ml-auto tl-btn tl-btn-ghost tl-btn-sm" onClick={() => act(r.period, r.closed ? "reopen" : "close")}>{r.closed ? "Rouvrir" : "Clôturer"}</button>}
        </div>
      ))}
    </div>
  );
}
